import React, { useEffect, useState, useRef } from "react";
import BackgroundHeader from "../../assets/background_header.svg";
import Close from "../../assets/close.svg";
import SendIcon from "../../assets/send-icon.svg";
import AbortIcon from "../../assets/abort-icon.svg";
import ChatWidgetConfig from "../../config/ChatWidgetConfig";
import "./styles.css";
import Footer from "../footer/Footer";
import PromptCard from "../card/PromptCard";
import TextArea from "../input/Textarea";
import SendButton from "../buttons/SendButton";
import DateSeparator from "../timestamp/DateComponent";
import TypingIndicator from "../loader/TypingLoader";
import ChatBubble from "../chat/ChatBubble";
import AcknowledgementModal from "../modal/AcknowledgementModal";
import { getExtendedApiWrapper } from "../../services/api-handlers";

const Container = ({
  appLogo = ChatWidgetConfig.appLogo,
  headerText = ChatWidgetConfig.headerText,
  onClose = ChatWidgetConfig.onClose,
  username = ChatWidgetConfig.username,
  primaryButtonText = ChatWidgetConfig.primaryButtonText,
  secondaryButtonText = ChatWidgetConfig.secondaryButtonText,
  acknowledgementContent = ChatWidgetConfig.acknowledgmentContent,
  programName = ChatWidgetConfig.programName,
  top = ChatWidgetConfig.top,
}) => {
  const [suggestionPrompts, setSuggestionPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [abortController, setAbortController] = useState(null);
  const [isResponding, setIsResponding] = useState(false);
  const [acknowledgmentStatus, setAcknowledgmentStatus] = useState(null);
  const [error, setError] = useState(null);

  const isMounted = useRef(true);
  const api = getExtendedApiWrapper();

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.getAcknowledgementStatus();
        if (response.data) {
          setAcknowledgmentStatus(response.data.acknowledged);
        }
      } catch (err) {
        setError(err.message);
      }
    };
    const fetchChatHistory = async () => {
      try {
        const response = await api.getChatHistory(
          100,
          0,
          programName
        );
        if (response.status === 200 && response.data) {
          const reversedChatHistory =
            response.data?.chat_history?.slice().reverse() || [];
          setMessages(reversedChatHistory);
        }
      } catch (error) {
        console.error("Failed to fetch chat history:", error);
      }
    };

    const fetchPrompts = async () => {
      try {
        const response = await api.getSuggestionPrompts(
          2,
          programName
        );
        if (response.status === 200) {
          setSuggestionPrompts(response.data.data);
        }
      } catch (err) {
        console.log("Failed to fetch prompts");
      }
    };
    fetchStatus();
    fetchChatHistory();
    fetchPrompts();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handlePromptClick = (prompt) => {
    setSelectedPrompt(prompt);
    handleSendMessage(prompt);
  };

  const handleUserInput = (input) => {
    setUserInput(input);
  };

  const handleSendMessage = async (messageInput = userInput) => {
    messageInput = String(messageInput || "").trim();
    if (!messageInput) return;

    const newMessage = { user_type: "user", message: messageInput };
    setMessages((prev) => [...prev, newMessage]);
    setUserInput("");
    setIsTyping(true);
    setIsResponding(true);

    // Create a new AbortController for this request
    const controller = new AbortController();
    setAbortController(controller);

    api.sendMessageWithSSE(
      messageInput,
      (newText) => {
        if (!isMounted.current) return;
        setIsTyping(false);

        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          if (lastMessage?.user_type === "bot") {
            return prev.map((msg, index) =>
              index === prev.length - 1
                ? { ...msg, message: msg.message + newText }
                : msg
            );
          } else {
            return [...prev, { user_type: "bot", message: newText }];
          }
        });
      },
      (error) => {
        console.error("SSE error:", error);
        setIsTyping(false);
        setIsResponding(false);
      },
      (messageId) => {
        if (isMounted.current) {
          setIsTyping(false);
          setIsResponding(false);
          setMessages((prev) =>
            prev.map((msg, index) =>
              index === prev.length - 1 && msg.user_type === "bot"
                ? { ...msg, message_id: messageId }
                : msg
            )
          );
        }
      },
      controller.signal,
      programName,
      "user"
    );
  };

  const handleCancelRequest = () => {
    if (abortController) {
      abortController.abort();
      setIsTyping(false);
      setIsResponding(false);
      setAbortController(null);
    }
  };

  const handleAcknowledgement = async (approved) => {
    try {
      await api.sendAcknowledgement(approved);
      if (approved) {
        setAcknowledgmentStatus(true);
      }
    } catch (error) {
      console.error("Error sending acknowledgment:", error);
    }
  };

  return (
    <div className="wrapper" style={{ top }}>
      {acknowledgmentStatus === false && (
        <AcknowledgementModal
          username={username}
          acknowledgementContent={acknowledgementContent}
          primaryButtonText={primaryButtonText}
          secondaryButtonText={secondaryButtonText}
          onDeclineClick={onClose}
          onAcknowledgeClick={() => handleAcknowledgement(true)}
        />
      )}
      <div
        className="header-top"
        style={{ backgroundImage: `url(${BackgroundHeader})` }}
      >
        <div className="header-wrapper">
          <img
            className="app-logo"
            src={appLogo}
            alt="app logo"
            loading="lazy"
            width={32}
            height={32}
          />
          <div className="text-overlay">{headerText}</div>
        </div>
        <button className="button" onClick={onClose}>
          <img src={Close} alt="close" loading="lazy" />
        </button>
      </div>
      <div className="chat-content">
        {!userInput && !selectedPrompt && messages.length === 0 && (
          <div className="prompt-container">
            {Array.isArray(suggestionPrompts) &&
              suggestionPrompts.map((prompt, index) => (
                <PromptCard
                  key={index}
                  prompt={prompt.text}
                  onClick={handlePromptClick}
                />
              ))}
          </div>
        )}
        {/* <DateSeparator /> */}
        <ChatBubble
          programName={programName}
          username={username}
          messages={messages}
          initialFeedback={messages.map((msg) => ({
            liked: msg.feedback === 0,
            disliked: msg.feedback === 1,
          }))}
        />
        {isTyping && <TypingIndicator />}
      </div>
      <div style={{ height: "27%" }}>
        <div className="input-container">
          <TextArea
            value={userInput}
            onChange={handleUserInput}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault(); // Prevents new line
                handleSendMessage(userInput);
              }
            }}
          />
          <SendButton
            onClick={
              isResponding
                ? handleCancelRequest
                : () => handleSendMessage(userInput)
            }
            bgColor={isResponding ? "#2B5580" : "#2251ff"}
            iconSrc={isResponding ? AbortIcon : SendIcon}
          />
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default Container;
